/**
 * WorkflowConfigTable — renders namespace-grouped config entries in table cards.
 */

import type { ConfigEntry } from "./types";
import styles from "./workflow.module.scss";

const NAMESPACE_META: Record<string, { label: string; description: string; color: string }> = {
  databases: {
    label: "Databases",
    description: "DB connection strings (Snowflake, Postgres, ClickHouse)",
    color: "#6366f1",
  },
  apis: {
    label: "APIs",
    description: "API keys and endpoints",
    color: "#10b981",
  },
  slack: {
    label: "Slack",
    description: "Slack tokens and channel configs",
    color: "#f59e0b",
  },
  agent: {
    label: "Agent",
    description: "Agent runtime config (model, temperature, etc.)",
    color: "#8b5cf6",
  },
};

interface Props {
  allNamespaces: string[];
  grouped: Record<string, ConfigEntry[]>;
  revealedIds: Set<string>;
  onToggleReveal: (id: string) => void;
  onEdit: (entry: ConfigEntry) => void;
  onDelete: (entry: ConfigEntry) => void;
  isDeletePending: boolean;
}

export const WorkflowConfigTable = ({
  allNamespaces,
  grouped,
  revealedIds,
  onToggleReveal,
  onEdit,
  onDelete,
  isDeletePending,
}: Props) => {
  return (
    <>
      {allNamespaces.map(ns => {
        const meta = NAMESPACE_META[ns];
        const entries = grouped[ns] ?? [];
        const color = meta?.color ?? "#6b7280";

        return (
          <div key={ns} className={styles.nsCard} style={{ borderTopColor: color }}>
            <div className={styles.nsCardHeader}>
              <div>
                <h3 className={styles.nsCardTitle} style={{ color }}>
                  {meta?.label ?? ns}
                </h3>
                {meta?.description && (
                  <p className={styles.nsCardDescription}>{meta.description}</p>
                )}
              </div>
              <span className={styles.nsCardCount}>
                {entries.length} {entries.length === 1 ? "entry" : "entries"}
              </span>
            </div>

            {entries.length === 0 ? (
              <div className={styles.nsCardEmpty}>No configurations in this namespace.</div>
            ) : (
              <table className={styles.configTable}>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(entry => (
                    <tr key={entry.id}>
                      <td className={styles.configKeyCell}>
                        {entry.key}
                        {entry.encrypted && (
                          <span className={styles.encryptedBadge}>encrypted</span>
                        )}
                      </td>
                      <td className={styles.configValueCell}>
                        {entry.encrypted && !revealedIds.has(entry.id) ? (
                          <span className={styles.maskedValue}>
                            {"••••••••"}
                            <button
                              type="button"
                              className={styles.revealBtn}
                              onClick={() => onToggleReveal(entry.id)}
                              title="Value is encrypted — cannot reveal"
                              disabled
                            >
                              show
                            </button>
                          </span>
                        ) : (
                          <code>
                            {typeof entry.value === "string"
                              ? entry.value
                              : JSON.stringify(entry.value)}
                          </code>
                        )}
                      </td>
                      <td className={styles.configTimeCell}>
                        {entry.updated_at
                          ? new Date(entry.updated_at).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "\u2014"}
                      </td>
                      <td className={styles.configActionsCell}>
                        <button
                          type="button"
                          className={styles.editBtn}
                          onClick={() => onEdit(entry)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={styles.deleteBtn}
                          onClick={() => onDelete(entry)}
                          title="Delete"
                          disabled={isDeletePending}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </>
  );
};
